import { useEffect, useState } from 'react';
import { getAssetUrl, getRawAssetUrl } from '../Api';
import { aguardar } from '../components/Loading';

const isGifContentType = (contentType) =>
  !!contentType && contentType.toLowerCase().includes('image/gif');

// Só lê o header Content-Type e cancela o corpo -- mais barato que baixar o
// arquivo (o original de alguns desses assets passa de 30MB).
const detectContentType = async (url) => {
  try {
    const response = await fetch(url);
    const contentType = response.headers.get('content-type');
    response.body?.cancel?.();
    return contentType;
  } catch {
    return null;
  }
};

// Para <img> normais dá pra usar onError direto na tag. Para background-image
// (CSS) não existe esse evento, então testamos a URL redimensionada em memória
// e, se ela falhar, trocamos para a original -- sem isso, um parâmetro de
// resize não suportado faz a imagem de fundo simplesmente não aparecer.
//
// opts.avoidWebpForGif: GIFs animados perdem a animação se o Squidex precisar
// recodificar pra webp no resize (o resultado fica achatado no primeiro
// frame). Quando essa opção está ativa, a gente checa o Content-Type real do
// arquivo antes de decidir se aplica o format:'webp' pedido em opts --
// usa webp só quando o arquivo não for gif, e mantém sem format (preservando
// a animação) quando for.
export const useResilientAssetUrl = (assetId, opts = {}) => {
  const { avoidWebpForGif, ...restOpts } = opts;

  const requestedUrl = getAssetUrl(assetId, restOpts);
  const plainUrl = getAssetUrl(assetId, { ...restOpts, format: undefined });
  const rawUrl = getRawAssetUrl(assetId);

  const initialUrl = avoidWebpForGif ? plainUrl : requestedUrl;
  const [url, setUrl] = useState(initialUrl);

  useEffect(() => {
    if (!assetId) {
      setUrl('');
      return;
    }

    let cancelled = false;
    setUrl(initialUrl);

    const probe = new Image();
    probe.onerror = () => {
      if (!cancelled) setUrl(rawUrl);
    };
    probe.src = initialUrl;

    if (avoidWebpForGif) {
      // A tela de carregamento espera esta resposta: é ela que decide o
      // endereço final da imagem (e a tela não pode sair antes de ele carregar).
      aguardar(detectContentType(rawUrl)).then((contentType) => {
        if (!cancelled && !isGifContentType(contentType)) {
          setUrl(requestedUrl);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [assetId, initialUrl, requestedUrl, rawUrl, avoidWebpForGif]);

  return url;
};
